import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { toast } from 'react-toastify';
import { api } from '../services/AuthService';
import BrowserPrintService from '../services/BrowserPrintService';
import {
  bootstrapOrderPrintTracking,
  loadPersistedPrintedIds,
  persistPrintedId,
  removePersistedPrintedId,
  recentEligibleOrders,
  shouldAutoPrintOrder,
  shouldPrintOnOrderPoll,
  wasPrintedPersisted
} from '../utils/orderPrintRules';

const getSocketServerUrl = () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';

  try {
    const url = new URL(apiUrl);
    return url.origin;
  } catch (_) {
    return apiUrl.replace(/\/api\/?$/, '').replace(/\/$/, '');
  }
};

export function useAutoPrint({ lanchonete, enabled = true, showToasts = true, pollIntervalMs = 30000 } = {}) {
  const lanchoneteRef = useRef(lanchonete);
  const pedidosImpressosRef = useRef(new Set());
  const pedidosConhecidosRef = useRef(new Set());
  const pedidoStatusRef = useRef(new Map());
  const primeiraCargaRef = useRef(false);
  const socketRef = useRef(null);
  const pollInFlightRef = useRef(false);
  const [connected, setConnected] = useState(false);
  const [lastPrintedId, setLastPrintedId] = useState(null);

  const tracking = useMemo(() => ({
    knownIds: pedidosConhecidosRef.current,
    printedIds: pedidosImpressosRef.current,
    statusById: pedidoStatusRef.current
  }), []);

  useEffect(() => {
    lanchoneteRef.current = lanchonete;
  }, [lanchonete]);

  const handleAutoPrint = useCallback(async (pedido) => {
    if (!enabled) return false;
    const pedidoId = Number(pedido?.id);
    const lanchoneteId = lanchoneteRef.current?.id;
    if (!Number.isInteger(pedidoId) || pedidosImpressosRef.current.has(pedidoId)) return false;
    if (wasPrintedPersisted(lanchoneteId, pedidoId)) {
      pedidosImpressosRef.current.add(pedidoId);
      return false;
    }
    if (!lanchoneteRef.current?.auto_print) return false;
    if (!shouldAutoPrintOrder(pedido)) return false;

    try {
      // Reserva antes de chamar window.print para outro listener/aba nao imprimir a segunda via.
      pedidosImpressosRef.current.add(pedidoId);
      persistPrintedId(lanchoneteId, pedidoId);
      const pedidoCompleto = pedido.items ? pedido : (await api.get(`/pedidos/${pedidoId}`)).data;
      const success = await BrowserPrintService.imprimirPedido(
        pedidoCompleto,
        lanchoneteRef.current,
        { automatic: true }
      );

      if (success) {
        setLastPrintedId(pedidoId);
        if (showToasts) {
          toast.success(`Pedido #${pedido.id} enviado para impressao automatica`);
        }
        return true;
      }

      pedidosImpressosRef.current.delete(pedidoId);
      removePersistedPrintedId(lanchoneteId, pedidoId);
      if (showToasts) toast.error('Falha na impressao automatica');
      return false;
    } catch (error) {
      pedidosImpressosRef.current.delete(pedidoId);
      removePersistedPrintedId(lanchoneteId, pedidoId);
      console.error('Erro na impressao automatica:', error);
      if (showToasts) toast.error('Erro na impressao automatica');
      return false;
    }
  }, [enabled, showToasts]);

  useEffect(() => {
    if (!enabled || !lanchonete?.id || !lanchonete?.auto_print) {
      setConnected(false);
      return undefined;
    }

    const token = localStorage.getItem('@DeliveryAdmin:token');
    if (!token) return undefined;

    const socket = io(getSocketServerUrl(), { auth: { token } });
    socketRef.current = socket;

    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));

    socket.on('novo_pedido', async (data) => {
      const pedido = data?.data;
      if (!pedido?.id) return;

      if (!pedidosConhecidosRef.current.has(pedido.id)) {
        pedidosConhecidosRef.current.add(pedido.id);
        pedidoStatusRef.current.set(pedido.id, String(pedido.status || '').trim().toLowerCase());
      }

      await handleAutoPrint(pedido);
    });

    socket.on('status_update', async (data) => {
      if (!data?.pedidoId || data.status !== 'pendente') return;
      if (pedidosImpressosRef.current.has(data.pedidoId)) return;

      try {
        const response = await api.get(`/pedidos/${data.pedidoId}`);
        const pedido = response.data;
        pedidoStatusRef.current.set(pedido.id, String(pedido.status || '').trim().toLowerCase());
        await handleAutoPrint(pedido);
      } catch (error) {
        console.error('Erro ao imprimir apos confirmacao de pagamento:', error);
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [enabled, lanchonete?.id, lanchonete?.auto_print, handleAutoPrint]);

  useEffect(() => {
    if (!enabled || !lanchonete?.id || !lanchonete?.auto_print) {
      return undefined;
    }

    const processarPedidos = async (pedidos) => {
      if (!primeiraCargaRef.current) {
        loadPersistedPrintedIds(lanchonete.id).forEach((id) => pedidosImpressosRef.current.add(id));
        // Recupera pedidos recentes caso o socket tenha conectado depois da
        // criacao do pedido ou o navegador tenha sido recarregado nesse meio-tempo.
        const recoverable = recentEligibleOrders(pedidos, pedidosImpressosRef.current);
        bootstrapOrderPrintTracking(pedidos, tracking);
        recoverable.forEach((pedido) => pedidosImpressosRef.current.delete(pedido.id));
        primeiraCargaRef.current = true;
        for (const pedido of recoverable) await handleAutoPrint(pedido);
        return;
      }

      for (const pedido of pedidos) {
        if (shouldPrintOnOrderPoll(pedido, tracking)) {
          await handleAutoPrint(pedido);
        }
      }
    };

    const pollPedidos = async () => {
      if (pollInFlightRef.current) return;
      pollInFlightRef.current = true;
      try {
        // O socket entrega os pedidos em tempo real. O polling e somente uma
        // recuperacao de seguranca e precisa apenas dos pedidos mais recentes.
        const response = await api.get(`/pedidos?lanchonete_id=${lanchonete.id}&limit=50`);
        await processarPedidos(response.data || []);
      } catch (error) {
        console.error('Erro ao buscar pedidos para impressao automatica:', error);
      } finally {
        pollInFlightRef.current = false;
      }
    };

    pollPedidos();
    const interval = setInterval(pollPedidos, pollIntervalMs);
    return () => clearInterval(interval);
  }, [enabled, lanchonete?.id, lanchonete?.auto_print, handleAutoPrint, pollIntervalMs, tracking]);

  return {
    handleAutoPrint,
    connected,
    lastPrintedId,
    socketRef,
    pedidosImpressosRef
  };
}
